/**
 * Resolucao de colisoes da timeline.
 *
 * Duas coisas a acontecer ao mesmo tempo: os pins ocupam a sua data exacta, e
 * as etiquetas precisam de espaco que a data nao lhes da. A solucao do design
 * e deixar a etiqueta desviar-se e ligar-se ao ponto por um traco fino — mas
 * so quando precisa, porque um desvio desnecessario e ruido.
 */

export interface PinInput {
  readonly id: string;
  /** Posicao do ponto: e a data, e nao se mexe. */
  readonly x: number;
  /** Largura do intervalo em px. 0 = data exacta. */
  readonly width: number;
  readonly labelWidth: number;
  /** Mais alto = mais perto do eixo, para o que importa ficar legivel. */
  readonly priority?: number;
}

export interface PlacedPin {
  readonly id: string;
  readonly x: number;
  readonly width: number;
  readonly row: number;
  readonly labelX: number;
  /** Verdadeiro quando a etiqueta saiu de cima do ponto e precisa de traco. */
  readonly deviated: boolean;
}

export interface LayoutOptions {
  readonly rows: number;
  readonly gap?: number;
  readonly bounds?: readonly [number, number];
}

interface Occupied { readonly from: number; readonly to: number }

const overlaps = (a: Occupied, b: Occupied, gap: number) =>
  a.from < b.to + gap && b.from < a.to + gap;

/**
 * Coloca cada pin numa linha e decide se a etiqueta se desvia.
 *
 * Estrategia, por ordem: manter a etiqueta centrada no ponto na primeira linha
 * livre; se nenhuma linha a aceita centrada, empurra-la para a direita do
 * ultimo ocupante da linha menos cheia e marcar o desvio.
 */
export function layoutPins(pins: readonly PinInput[], options: LayoutOptions): PlacedPin[] {
  const gap = options.gap ?? 8;
  const rows = Math.max(1, options.rows);
  const bounds = options.bounds;

  const ordered = [...pins].sort((a, b) =>
    (b.priority ?? 0) - (a.priority ?? 0) || a.x - b.x || a.id.localeCompare(b.id));

  const occupied: Occupied[][] = Array.from({ length: rows }, () => []);
  const placed: PlacedPin[] = [];

  const fits = (row: number, box: Occupied) =>
    !occupied[row]!.some((o) => overlaps(o, box, gap));

  const clampLabel = (centre: number, width: number): number => {
    if (bounds === undefined) return centre;
    const half = width / 2;
    return Math.min(Math.max(centre, bounds[0] + half), bounds[1] - half);
  };

  for (const pin of ordered) {
    const half = pin.labelWidth / 2;
    const centred = clampLabel(pin.x + pin.width / 2, pin.labelWidth);
    const box = { from: centred - half, to: centred + half };

    let row = -1;
    for (let r = 0; r < rows; r += 1) {
      if (fits(r, box)) { row = r; break; }
    }

    if (row >= 0) {
      occupied[row]!.push(box);
      placed.push({ id: pin.id, x: pin.x, width: pin.width, row, labelX: centred, deviated: false });
      continue;
    }

    // Nenhuma linha aceita a etiqueta centrada: desvia-se na linha mais folgada.
    let bestRow = 0;
    let bestEdge = Infinity;
    for (let r = 0; r < rows; r += 1) {
      const edge = occupied[r]!.reduce((max, o) => Math.max(max, o.to), -Infinity);
      if (edge < bestEdge) { bestEdge = edge; bestRow = r; }
    }
    const from = bestEdge === -Infinity ? box.from : bestEdge + gap;
    const shifted = { from, to: from + pin.labelWidth };
    occupied[bestRow]!.push(shifted);
    placed.push({
      id: pin.id, x: pin.x, width: pin.width, row: bestRow,
      labelX: shifted.from + half,
      // So conta como desvio se a etiqueta saiu mesmo de cima do ponto.
      deviated: Math.abs((shifted.from + half) - centred) > 1,
    });
  }

  return placed.sort((a, b) => a.x - b.x || a.id.localeCompare(b.id));
}

// ── virtualizacao ────────────────────────────────────────────

export interface Visible<T> {
  readonly items: readonly T[];
  readonly hidden: number;
}

/**
 * So o que esta no viewport, com margem. E o que permite ter 400 pins na base
 * e desenhar algumas dezenas.
 */
export function visibleInRange<T extends { x: number; width?: number }>(
  items: readonly T[], range: readonly [number, number], margin = 200,
): Visible<T> {
  const [lo, hi] = [range[0] - margin, range[1] + margin];
  const kept = items.filter((i) => i.x + (i.width ?? 0) >= lo && i.x <= hi);
  return { items: kept, hidden: items.length - kept.length };
}

// ── clustering ───────────────────────────────────────────────

export interface Cluster<T> {
  readonly x: number;
  readonly items: readonly T[];
}

/**
 * Acima de um limiar, pins proximos juntam-se num so com contagem. Nunca
 * escondemos itens em silencio: o cluster mostra quantos la estao.
 */
export function clusterPins<T extends { x: number }>(
  items: readonly T[], minDistance: number, threshold: number,
): Cluster<T>[] {
  if (items.length <= threshold) return items.map((i) => ({ x: i.x, items: [i] }));

  const sorted = [...items].sort((a, b) => a.x - b.x);
  const out: Cluster<T>[] = [];
  let bucket: T[] = [];
  let anchor = -Infinity;

  for (const item of sorted) {
    if (bucket.length > 0 && item.x - anchor > minDistance) {
      out.push({ x: bucket.reduce((n, i) => n + i.x, 0) / bucket.length, items: bucket });
      bucket = [];
    }
    if (bucket.length === 0) anchor = item.x;
    bucket.push(item);
  }
  if (bucket.length > 0) {
    out.push({ x: bucket.reduce((n, i) => n + i.x, 0) / bucket.length, items: bucket });
  }
  return out;
}

// ── banda de densidade ───────────────────────────────────────

export interface DensityBucket {
  readonly x: number;
  readonly count: number;
}

/** Agregado para a banda dentro da barra escura, quando o zoom afasta. */
export function densityBuckets(
  xs: readonly number[], range: readonly [number, number], bucketWidth = 6,
): DensityBucket[] {
  const [lo, hi] = range;
  const n = Math.max(1, Math.ceil((hi - lo) / bucketWidth));
  const counts = new Array<number>(n).fill(0);
  for (const x of xs) {
    if (x < lo || x > hi) continue;
    const i = Math.min(n - 1, Math.floor((x - lo) / bucketWidth));
    counts[i] = (counts[i] ?? 0) + 1;
  }
  return counts
    .map((count, i) => ({ x: lo + i * bucketWidth, count }))
    .filter((b) => b.count > 0);
}
